import { zodResolver } from "@hookform/resolvers/zod";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, Button } from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import uuid from "uuid";

import { StickyFooter } from "components/stickyFooter/footer";
import { FormFooterWrapper } from "components/stickyFooter/FormFooterWrapper";
import { ViewCancelButton } from "components/stickyFooter/ViewCancelButton";
import {
    buildTestingProgramTestingControlsListUrl,
    testingProgramListUrl,
} from "org/controlTesting/urlUtils";
import { useCurrentUserOverview } from "utils/hooks/useCurrentUserOverview";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import { useAvailableActions } from "../../../../utils/hooks/useAvailableActions";

import { useTestingProgramViewAnchors } from "./consts";
import { useCreateTestingProgramMutation } from "./queries/useCreateTestingProgramMutation";
import { TestingProgramForm } from "./TestingProgramForm";
import {
    TestingProgramFormSchema,
    useTestingProgramViewFormSchema,
} from "./useTestingProgramViewFormSchema";

export function TestingProgramCreatePage() {
    const { t } = useTranslation();
    const anchors = useTestingProgramViewAnchors();
    const canCreate = useAvailableActions().testingProgram.create;

    if (!canCreate) {
        return <LoggedInHTTPError code={403} />;
    }

    return (
        <OrgApplicationBar
            title={{
                title: t("ControlTesting_CreateTitle"),
                breadCrumbs: [
                    {
                        label: t("ControlTesting_ModuleTitle"),
                        url: testingProgramListUrl,
                    },
                ],
            }}
            navigation={{
                type: "Anchors",
                anchors,
            }}
        >
            <View />
        </OrgApplicationBar>
    );
}

function View() {
    const { t } = useTranslation();
    const schema = useTestingProgramViewFormSchema();
    const currentUser = useCurrentUserOverview();
    const { mutateAsync, isPending } = useCreateTestingProgramMutation();
    const navigate = useNavigate();

    const { control, formState, handleSubmit } =
        useForm<TestingProgramFormSchema>({
            resolver: zodResolver(schema),
            mode: "onBlur",
            defaultValues: {
                name: "",
                description: "",
                accessRights: [{ userId: currentUser?.id, right: "Admin" }],
                controlConclusionTasks: [
                    {
                        id: uuid.v4(),
                        blobIds: [],
                        condition: null,
                        mandatory: true,
                        instructions: "",
                        title: t("TestingProgramView_Effectiveness_Title"),
                        fieldType: {
                            type: "Effectiveness",
                            options: {
                                effectiveOptionName: t(
                                    "TestingProgramView_Effectiveness_EffectiveOption"
                                ),
                                partiallyEffectiveOptionName: t(
                                    "TestingProgramView_Effectiveness_PartiallyEffectiveOption"
                                ),
                                notEffectiveOptionName: t(
                                    "TestingProgramView_Effectiveness_NotEffectiveOption"
                                ),
                            },
                        },
                    },
                ],
                sampleConclusionTasks: [],
            },
        });

    const handleSubmitClick = handleSubmit(
        async (data) => {
            const response = await mutateAsync({ data });
            navigate(
                buildTestingProgramTestingControlsListUrl(response.data.id)
            );
        },
        (error) => {
            console.error(error);
            emitNotification(
                "error",
                t("TestingProgramView_SubmitValidationError")
            );
        }
    );
    if (!currentUser) {
        return null;
    }
    return (
        <FormFooterWrapper>
            <TestingProgramForm
                control={control}
                users={[currentUser]}
                blobs={[]}
                canEdit
                canEditAccessRights
            />
            <StickyFooter>
                <Button
                    size="large"
                    variant="primary"
                    disabled={!formState.isDirty}
                    loading={isPending}
                    onClick={() => void handleSubmitClick()}
                >
                    {t("TestingProgramView_CreateButton")}
                </Button>
                <ViewCancelButton
                    isDirty={formState.isDirty}
                    bodyLabel={t("TestingProgramView_ConfirmCancel_Body")}
                />
            </StickyFooter>
        </FormFooterWrapper>
    );
}
